import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "src/store";
import { showErrorToast, showSuccessToast } from "src/ui/shared/toasts/Toasts";

import { updatePolicy } from "@dashboard/devices/store";
import { Group } from "@devices/types";

interface UpdatePolicyProps {
    groupId: Group["id"];
    policy: string;
    onSuccess?: () => void;
}

export const usePolicyCommands = () => {
    const dispatch = useDispatch();
    const { t } = useTranslation(["modals"]);

    const [loadingState, setLoadingState] = useState({
        updatePolicyLoading: false,
        clearPolicyLoading: false,
    });

    const updatePolicyCommands = async ({ groupId, policy, onSuccess }: UpdatePolicyProps) => {
        setLoadingState({ ...loadingState, updatePolicyLoading: true });

        try {
            await dispatch(updatePolicy({ groupId, policy }))
                .unwrap()
                .then(() => {
                    showSuccessToast(t("modals:newPolicyCommand.toast.policyCommandsUpdated"));
                    if (onSuccess) {
                        onSuccess();
                    }
                });
        } catch (error) {
            const err = error as Error;
            showErrorToast(err.message ?? t("modals:newPolicyCommand.toast.policyCommandsUpdateFailed"));
        } finally {
            setLoadingState({ ...loadingState, updatePolicyLoading: false });
        }
    };
    const clearPolicyCommands = async ({ groupId, policy }: UpdatePolicyProps) => {
        setLoadingState({ ...loadingState, clearPolicyLoading: true });

        try {
            await dispatch(updatePolicy({ groupId, policy }))
                .unwrap()
                .then(() => {
                    showSuccessToast(t("modals:newPolicyCommand.toast.policyDeleted"));
                });
        } catch (error) {
            const err = error as Error;
            showErrorToast(err.message ?? t("modals:newPolicyCommand.toast.policyCommandsDeletionFailed"));
        } finally {
            setLoadingState({ ...loadingState, clearPolicyLoading: false });
        }
    };

    return { updatePolicyCommands, clearPolicyCommands, loadingState };
};
